
import { getFirestore, collection, getDocs, query, orderBy, limit, where, startAt, endAt } from "firebase/firestore";
import { app } from "../firebase-config.js";

const db = getFirestore(app);

export const SearchService = {
    /**
     * Server-side Search using Firestore Queries.
     * Replaces the heavy client-side index.
     * 
     * Strategy:
     * 1. Title Prefix Search (Case-sensitive in Firestore, so we might need a lowercase field in DB ideally)
     * 2. Tag Search (Array contents)
     * 3. Author Search (If simple field)
     * 
     * Note: Firestore doesn't support "OR" across different fields easily without Algolia.
     * We will execute parallel queries and merge results.
     * 
     * @param {string} queryStr 
     * @returns {Promise<Array>}
     */
    searchSeries: async (queryStr) => {
        if (!queryStr || queryStr.trim().length === 0) return [];

        const term = queryStr.trim();
        const termLower = term.toLowerCase(); // For Tag search if tags are lowercase

        // [FIX] Reverse Mapping for Localized Tags
        // If user searches "Fantasy", we must also search "판타지"
        const REVERSE_TAG_MAP = {
            "fantasy": "판타지",
            "romance": "로맨스",
            "martial arts": "무협", "martial_arts": "무협",
            "drama": "드라마",
            "slice of life": "일상", "slice_of_life": "일상",
            "comedy": "개그",
            "thriller": "스릴러",
            "school": "학원물",
            "munchkin": "먼치킨",
            "regression": "회귀",
            "possession": "빙의",
            "system": "시스템",
            "growth": "성장",
            "revenge": "복수"
        };

        let searchTerms = [term];
        if (REVERSE_TAG_MAP[termLower]) {
            searchTerms.push(REVERSE_TAG_MAP[termLower]);
        }

        try {
            // 1. Title Search (Prefix)
            // Note: Must filter by 'status' == 'published' to satisfy Rules for Public Access.
            // Firestore requires specific composite index for (status, title).
            const titleQuery = query(
                collection(db, "series"),
                where("status", "==", "published"), // [FIX] Security Rule Compliance
                orderBy("title"),
                startAt(term),
                endAt(term + "\uf8ff"),
                limit(10)
            );

            // 1.5 Author Search (Prefix)
            // Note: Requires composite index (status, authorName)
            const authorQuery = query(
                collection(db, "series"),
                where("status", "==", "published"),
                orderBy("authorName"),
                startAt(term),
                endAt(term + "\uf8ff"),
                limit(10)
            );

            // 2. Tag Search
            // Note: Must filter by 'status' == 'published' to satisfy Rules for Public Access.
            // [FIX] Support Multiple Terms (OR Logic via Parallel Queries)
            const tagQueries = searchTerms.map(t => query(
                collection(db, "series"),
                where("status", "==", "published"), // [FIX] Security Rule Compliance
                where("tags", "array-contains", t),
                limit(10)
            ));

            // Execute Parallel
            const [titleSnap, authorSnap, ...tagSnaps] = await Promise.all([
                getDocs(titleQuery),
                getDocs(authorQuery),
                ...tagQueries.map(q => getDocs(q))
            ]);

            // Merge & Deduplicate
            const resultMap = new Map();

            const processDoc = (doc) => {
                if (!resultMap.has(doc.id)) {
                    const data = doc.data();
                    resultMap.set(doc.id, {
                        id: doc.id,
                        title: data.title,
                        // Normalize for SearchResultsGrid
                        authorName: data.authorName || 'Unknown',
                        thumbnail: data.coverImage || data.thumbnail,
                        description: data.description,
                        tags: data.tags || [],
                        type: data.type || 'webtoon',
                        stats: data.stats || {},
                        // Add Author Photo logic if needed, but generic card handles it usually
                        authorPhoto: data.authorPhoto || null
                    });
                }
            };

            titleSnap.forEach(processDoc);
            titleSnap.forEach(processDoc);
            authorSnap.forEach(processDoc);
            tagSnaps.forEach(snap => snap.forEach(processDoc));
            tagSnaps.forEach(snap => snap.forEach(processDoc));

            return Array.from(resultMap.values());

        } catch (e) {
            console.error("SearchService Error:", e);
            return [];
        }
    },

    /**
     * loadSearchIndex (Depreacted/Stub)
     * Kept for compatibility if other components call it, but does nothing.
     */
    loadSearchIndex: async () => {
        // No-op
        return [];
    },

    /**
     * performSearch (Compatibility Wrapper)
     * Returns Promise now, warning if used synchronously.
     */
    performSearch: async (queryStr) => {
        return SearchService.searchSeries(queryStr);
    }
};

